import { GppGoodOutlined } from '@mui/icons-material';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { designTokens } from '../../../design-system';
import {
  certificatePurpose,
  certificateSheetDate,
} from '../model/presentation';
import type { CertificateIssueInput, CertificatePreview } from '../model/types';
import './CertificateSheet.css';

export function CertificateSheet({
  preview,
  purposeCode,
  destination,
}: {
  preview: CertificatePreview;
  purposeCode: CertificateIssueInput['purposeCode'];
  destination: string;
}) {
  const snapshot = preview.snapshot;
  const today = snapshot.issuedOn ?? new Date().toISOString().slice(0, 10);
  const rows = [
    ['성명', snapshot.legalName],
    ['주민등록번호', snapshot.maskedRegistrationNumber],
    ['직원유형', snapshot.workerCategory],
    ['재직기간', `${certificateSheetDate(snapshot.hireDate)} ~ 현재`],
    ['직책', snapshot.businessTitle || '-'],
    ['부서', snapshot.organizationName || '-'],
    ['용도', certificatePurpose(purposeCode, destination)],
  ];
  const issuerName = snapshot.companyName;
  const sealLabel = snapshot.sealConfigured ? '(직인)'
    : preview.template.requireSeal ? '직인 미등록' : '직인 생략';

  return <Paper
    aria-label="증명서 미리보기"
    className="certificate-sheet"
    variant="outlined"
    sx={{
      width: '100%', maxWidth: 560, aspectRatio: '210 / 297', mx: 'auto', bgcolor: designTokens.color.canvas, borderColor: designTokens.color.strongDivider,
      boxShadow: `0 18px 48px ${designTokens.overlay.detailBackdrop}`, borderRadius: 0.75, px: { xs: 3, sm: 5.5 }, py: { xs: 3, sm: 5 },
      display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
    }}
  >
    <Typography variant="caption" sx={{ color: designTokens.color.primaryText, textAlign: 'right', fontSize: { xs: 9, sm: 12 } }}>
      발급번호 : 발급 시 부여
    </Typography>
    <Typography sx={{ mt: { xs: 2.5, sm: 4 }, fontSize: { xs: 21, sm: 28 }, fontWeight: 800, color: designTokens.color.primaryText }}>
      {preview.template.displayName}
    </Typography>
    <Typography aria-hidden sx={{ position: 'absolute', top: '43%', left: '50%', width: '80%', transform: 'translate(-50%, -50%)', color: designTokens.color.brandNavy, opacity: 0.055, fontSize: { xs: 24, sm: 38 }, lineHeight: 1, fontWeight: 800, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', pointerEvents: 'none' }}>
      {issuerName}
    </Typography>
    <Box component="dl" sx={{ position: 'relative', zIndex: 1, m: 0, mt: { xs: 2.5, sm: 4 }, display: 'grid', gap: { xs: 1.25, sm: 2.2 } }}>
      {rows.map(([label, value]) => <Box key={label} component="div" sx={{ display: 'grid', gridTemplateColumns: { xs: '88px minmax(0, 1fr)', sm: '124px minmax(0, 1fr)' }, gap: 1.5, alignItems: 'baseline' }}>
        <Typography component="dt" variant="body2" sx={{ color: designTokens.color.primaryText, fontSize: { xs: 11, sm: 14 } }}>{label}</Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0, color: designTokens.color.primaryText, fontSize: { xs: 11, sm: 14 }, fontWeight: 500 }}>{value || '-'}</Typography>
      </Box>)}
    </Box>
    <Box sx={{ position: 'relative', zIndex: 1, mt: 'auto', display: 'grid', gridTemplateColumns: 'minmax(0, 0.8fr) minmax(0, 1.2fr)', gap: { xs: 1.5, sm: 3 }, alignItems: 'end' }}>
      <Box sx={{ minWidth: 0, pb: { xs: 0.5, sm: 1 } }}>
        <Typography sx={{ color: designTokens.color.brandNavy, fontSize: { xs: 8.5, sm: 12.5 }, lineHeight: 1.15, fontWeight: 850, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{issuerName}</Typography>
      </Box>
      <Stack spacing={{ xs: 0.45, sm: 0.75 }} sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ mb: { xs: 0.5, sm: 1 }, color: designTokens.color.primaryText, fontSize: { xs: 10.5, sm: 13 } }}>{preview.template.statement}</Typography>
        <Typography variant="body2" sx={{ color: designTokens.color.primaryText, fontSize: { xs: 10.5, sm: 13 } }}>{certificateSheetDate(today)}</Typography>
        <Typography variant="body2" sx={{ color: designTokens.color.primaryText, fontSize: { xs: 10.5, sm: 13 }, fontWeight: 650 }}>{issuerName}</Typography>
        <Typography variant="caption" sx={{ color: designTokens.color.primaryText, fontSize: { xs: 8.5, sm: 11 }, overflowWrap: 'anywhere' }}>{snapshot.registeredAddress || '-'}</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: designTokens.color.primaryText, fontSize: { xs: 10.5, sm: 13 }, fontWeight: 760 }}>
            {snapshot.representativeTitle || '대표자'}&nbsp;&nbsp;{snapshot.representativeName || '-'}
          </Typography>
          <Box
            aria-label={`직인 상태: ${sealLabel}`}
            sx={{
              width: { xs: 42, sm: 54 }, height: { xs: 42, sm: 54 }, borderRadius: '50%', border: `2px solid ${snapshot.sealConfigured ? designTokens.color.danger : designTokens.color.strongDivider}`,
              color: snapshot.sealConfigured ? designTokens.color.danger : designTokens.color.mutedText, display: 'grid', placeItems: 'center', textAlign: 'center', fontSize: { xs: 8, sm: 10 }, fontWeight: 760,
            }}
          >{sealLabel}</Box>
          {preview.template.verificationPublic && <Box aria-label="QR 진위확인" sx={{ width: { xs: 36, sm: 42 }, height: { xs: 36, sm: 42 }, border: `1px solid ${designTokens.color.strongDivider}`, display: 'grid', placeItems: 'center', color: designTokens.color.brandNavy }}>
            <Stack spacing={0} sx={{ alignItems: 'center' }}><GppGoodOutlined sx={{ fontSize: { xs: 17, sm: 20 } }} /><Typography sx={{ fontSize: 7, lineHeight: 1 }}>QR</Typography></Stack>
          </Box>}
        </Stack>
      </Stack>
    </Box>
  </Paper>;
}
